/**
 * BoEleveDashboardPage — Hub back-office pour un élève.
 * Route : /bo/eleve
 *
 * Sections :
 *  - Bandeau bienvenue + fiche élève (formule, heures, enseignant assigné)
 *  - Stats visuelles (progrès moyen, heures faites/total, bilans reçus)
 *  - Prochaines leçons planifiées (3 max)
 *  - Historique récent avec bilans dépliables
 *  - Fréquence par compétence (heat-map light)
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Calendar, Clock, Car, GraduationCap, Sparkles, ChevronDown,
  Award, TrendingUp, BookOpen, AlertCircle, Smile, Meh, Frown, ExternalLink,
} from 'lucide-react';
import api from '../../api';

const BO_BLUE = '#1d4ed8';
const TYPE_LABEL = {
  conduite: 'Conduite', code: 'Code', examen: 'Examen',
  rvp: 'RVP', moto_plateau: 'Moto · Plateau',
};
const MOOD_META = {
  good: { icon: Smile, label: 'Confiant', color: '#059669' },
  neutral: { icon: Meh, label: 'Neutre', color: '#f59e0b' },
  needs_work: { icon: Frown, label: 'À revoir', color: '#dc2626' },
};

export default function BoEleveDashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openBilanId, setOpenBilanId] = useState(null);

  useEffect(() => {
    document.title = 'Mon espace · Code 2 Roues';
    (async () => {
      try {
        const [d, h] = await Promise.all([
          api.boEleveDashboard(),
          api.boEleveHistory().catch(() => ({ items: [] })),
        ]);
        setData(d);
        setHistory(h.items || []);
      } catch (e) {
        if (e.status === 401 || e.status === 403) {
          navigate('/login', { state: { from: '/bo/eleve' } });
          return;
        }
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-16 text-center text-foreground/50 text-sm">
        Chargement de ton espace…
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-16">
        <div className="rounded-lg border-2 border-red-500/40 bg-red-500/10 p-4 flex items-start gap-2 text-sm">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
          <div>{error}</div>
        </div>
      </div>
    );
  }

  if (!data?.linked) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="eleve-unlinked">
        <Link to="/bo" className="inline-flex items-center gap-1.5 text-xs font-bold text-foreground/60 hover:text-foreground mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Back-office
        </Link>
        <div className="rounded-2xl border-2 border-dashed border-border p-8 text-center">
          <BookOpen className="w-10 h-10 mx-auto mb-4 text-foreground/40" />
          <h2 className="text-xl font-black mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Ton compte n&apos;est pas encore lié.
          </h2>
          <p className="text-sm text-foreground/70 mb-4">
            {data?.message}
          </p>
          <p className="text-xs text-foreground/50">
            Demande à ton auto-école de créer ta fiche avec l&apos;email <strong>{data?.user_email}</strong> dans
            le secrétariat.
          </p>
        </div>
      </div>
    );
  }

  const { student, teacher, upcoming, stats, competences_frequency } = data;
  const maxFreq = Math.max(1, ...competences_frequency.map((c) => c.count));
  const progressPct = stats.hours_package
    ? Math.min(100, Math.round((stats.hours_done / stats.hours_package) * 100))
    : 0;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10" data-testid="bo-eleve-dashboard">
      <Link to="/bo" className="inline-flex items-center gap-1.5 text-xs font-bold text-foreground/60 hover:text-foreground mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Back-office
      </Link>

      {/* Header élève */}
      <div
        className="rounded-2xl border-2 p-5 sm:p-6 mb-6"
        style={{ borderColor: BO_BLUE, background: 'rgba(29,78,216,0.05)' }}
      >
        <div className="flex items-start gap-4">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center text-white flex-shrink-0 font-black text-lg"
            style={{ background: BO_BLUE }}
          >
            {(student.name || '?').slice(0, 2).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest mb-1" style={{ color: BO_BLUE }}>
              Mon espace élève
            </div>
            <h1 className="text-2xl sm:text-3xl font-black leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Bonjour, {student.name.split(' ')[0]}
            </h1>
            <div className="text-xs sm:text-sm text-foreground/70 mt-1">
              Formule : <strong>{student.formula}</strong>
              {teacher && (
                <>
                  {' · '}Moniteur :{' '}
                  <span
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-white text-[10px] font-black"
                    style={{ background: teacher.color || BO_BLUE }}
                  >
                    <GraduationCap className="w-3 h-3" /> {teacher.name}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats grille */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="eleve-stats">
        <StatBox label="Heures faites" value={`${stats.hours_done}/${stats.hours_package || '?'}`} icon={Clock} color={BO_BLUE} testid="stat-hours" />
        <StatBox label="Leçons effectuées" value={stats.lessons_done_count} icon={Award} color="#059669" testid="stat-done" />
        <StatBox label="À venir" value={stats.lessons_upcoming_count} icon={Calendar} color="#f59e0b" testid="stat-upcoming" />
        <StatBox
          label="Progression moy."
          value={stats.avg_progression ? `${stats.avg_progression}/5` : '—'}
          icon={TrendingUp}
          color="#7c3aed"
          testid="stat-progress"
        />
      </div>

      {/* Barre progression heures */}
      {stats.hours_package && (
        <div className="mb-6" data-testid="eleve-progress-bar">
          <div className="flex justify-between text-[10px] font-black uppercase tracking-widest text-foreground/60 mb-1">
            <span>Progression du forfait</span>
            <span>{progressPct}%</span>
          </div>
          <div className="h-3 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${progressPct}%`, background: BO_BLUE }}
            />
          </div>
        </div>
      )}

      {/* Prochaines leçons */}
      {upcoming.length > 0 && (
        <section className="mb-6" data-testid="eleve-upcoming">
          <h3 className="text-lg font-black mb-3 flex items-center gap-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            <Calendar className="w-4 h-4" style={{ color: BO_BLUE }} />
            Prochaines leçons ({upcoming.length})
          </h3>
          <div className="space-y-2">
            {upcoming.slice(0, 3).map((l) => (
              <div
                key={l.id}
                className="rounded-xl border-2 border-border bg-card p-3 sm:p-4 flex items-center gap-3"
                data-testid={`upcoming-${l.id}`}
              >
                <div
                  className="flex-shrink-0 text-center px-3 py-2 rounded-lg"
                  style={{ background: `${l.teacher_color || BO_BLUE}18` }}
                >
                  <div className="text-[9px] font-black uppercase tracking-widest" style={{ color: l.teacher_color || BO_BLUE }}>
                    {new Date(l.date).toLocaleDateString('fr-FR', { weekday: 'short' })}
                  </div>
                  <div className="text-lg font-black text-foreground leading-tight">
                    {new Date(l.date).getDate()}
                  </div>
                  <div className="text-[10px] text-foreground/60 font-black">{l.start_time}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-black">
                    {TYPE_LABEL[l.type] || l.type} · {l.duration_min}min
                  </div>
                  <div className="text-xs text-foreground/60 mt-0.5">avec {l.teacher_name}</div>
                </div>
                {l.vehicle_type && (
                  <span className="text-[10px] text-foreground/50 inline-flex items-center gap-1">
                    <Car className="w-3 h-3" /> {l.vehicle_type}
                  </span>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Historique + bilans */}
      {history.length > 0 && (
        <section className="mb-6" data-testid="eleve-history">
          <h3 className="text-lg font-black mb-3 flex items-center gap-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            <BookOpen className="w-4 h-4" style={{ color: BO_BLUE }} />
            Historique ({history.length} leçons)
          </h3>
          <div className="space-y-2">
            {history.slice(0, 8).map((l) => {
              const hasBilan = !!l.bilan;
              const open = openBilanId === l.id;
              const mood = MOOD_META[l.bilan?.mood] || MOOD_META.neutral;
              const MoodIcon = mood.icon;
              return (
                <div
                  key={l.id}
                  className="rounded-xl border-2 border-border bg-card overflow-hidden"
                  data-testid={`history-${l.id}`}
                >
                  <button
                    type="button"
                    onClick={() => hasBilan && setOpenBilanId(open ? null : l.id)}
                    className={`w-full p-3 sm:p-4 flex items-center gap-3 text-left ${hasBilan ? 'hover:bg-muted/30' : 'cursor-default'} transition`}
                  >
                    <div className="text-[10px] font-black uppercase tracking-widest text-foreground/50 flex-shrink-0 w-16">
                      {new Date(l.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold">
                        {TYPE_LABEL[l.type] || l.type} · {l.duration_min}min
                      </div>
                      <div className="text-xs text-foreground/60">avec {l.teacher_name}</div>
                    </div>
                    {hasBilan && (
                      <>
                        <span
                          className="text-[10px] font-black px-1.5 py-0.5 rounded text-white"
                          style={{ background: mood.color }}
                        >
                          <MoodIcon className="w-3 h-3 inline mr-0.5" />
                          {l.bilan.progression_note}/5
                        </span>
                        <ChevronDown className={`w-4 h-4 text-foreground/40 transition ${open ? 'rotate-180' : ''}`} />
                      </>
                    )}
                  </button>
                  {open && hasBilan && (
                    <div className="border-t border-border/60 p-3 sm:p-4 bg-muted/20 space-y-3" data-testid={`bilan-${l.id}`}>
                      {l.bilan.competences_travaillees?.length > 0 && (
                        <div>
                          <div className="text-[9px] font-black uppercase tracking-widest text-foreground/50 mb-1">
                            Compétences travaillées
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {l.bilan.competences_travaillees.map((c) => {
                              const comp = competences_frequency.find((x) => x.key === c);
                              return (
                                <span
                                  key={c}
                                  className="text-[10px] font-black px-1.5 py-0.5 rounded"
                                  style={{ background: 'rgba(29,78,216,0.1)', color: BO_BLUE }}
                                >
                                  ✓ {comp?.label || c}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {l.bilan.message && (
                        <div>
                          <div className="text-[9px] font-black uppercase tracking-widest text-foreground/50 mb-1">
                            Un mot de ton moniteur
                          </div>
                          <div className="text-sm text-foreground/80 italic border-l-2 border-[#1d4ed8] pl-3">
                            « {l.bilan.message} »
                          </div>
                        </div>
                      )}
                      <div className="text-[9px] text-foreground/40">
                        Bilan rempli le {new Date(l.bilan.created_at).toLocaleDateString('fr-FR', {
                          day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit',
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Heat-map compétences */}
      {competences_frequency.some((c) => c.count > 0) && (
        <section className="mb-6" data-testid="eleve-competences">
          <h3 className="text-lg font-black mb-3 flex items-center gap-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            <Sparkles className="w-4 h-4" style={{ color: BO_BLUE }} />
            Tes points travaillés
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {competences_frequency
              .filter((c) => c.count > 0)
              .sort((a, b) => b.count - a.count)
              .map((c) => {
                const pct = Math.round((c.count / maxFreq) * 100);
                return (
                  <div
                    key={c.key}
                    className="rounded-lg border border-border bg-card p-2.5"
                    data-testid={`comp-freq-${c.key}`}
                  >
                    <div className="flex justify-between items-baseline mb-1">
                      <span className="text-[11px] font-black">{c.label}</span>
                      <span className="text-[9px] font-black text-foreground/50">×{c.count}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: BO_BLUE }} />
                    </div>
                  </div>
                );
              })}
          </div>
        </section>
      )}

      <div className="text-center border-t border-border/50 pt-6">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-xs font-bold text-foreground/60 hover:text-foreground"
          data-testid="back-to-public"
        >
          <ExternalLink className="w-3.5 h-3.5" /> Retourner sur le site
        </Link>
      </div>
    </div>
  );
}

function StatBox({ label, value, icon: Icon, color, testid }) {
  return (
    <div className="rounded-xl border-2 border-border bg-card p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        <div className="text-[9px] font-black uppercase tracking-widest text-foreground/60">
          {label}
        </div>
      </div>
      <div className="text-xl sm:text-2xl font-black" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
        {value}
      </div>
    </div>
  );
}
